import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Utils from "../../utils";
import { runbookVersionActions } from "../../actions/runbookVersion.actions";
import { runbookActions } from "../../actions/runbook.actions";
import { runbookVersionService } from "../../services/runbookVersion.service";
import Grid from "../common/Grid";
import Title from "../common/Title";
import { CiBoxList } from "react-icons/ci";
import { FaCodeBranch, FaPlus, FaEdit, FaLock, FaUnlock, FaTrash, FaCheckCircle, FaUpload, FaExchangeAlt } from "react-icons/fa";
import { MdGppGood, MdGppBad } from "react-icons/md";
import Subtitle from "../common/Subtitle";
import useRunbookTitle from "../../hooks/runbookTitle";
import GridHelper from "../helpers/gridHelper";
import { Button, Modal, Form, Alert, Spinner, Badge, ButtonGroup } from "react-bootstrap";
import { usePermissions } from "../../hooks/usePermissions";
import RunbookVersionCompare from "./RunbookVersionCompare";

function RunbookVersionGrid(props) {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { hasPermission } = usePermissions(props.user);

  const runbookVersionReducer = useSelector(
    (state) => state.runbookVersion
  );
  const runbookReducer = useSelector((state) => state.runbook);
  const { runbookTitle, runbookTitleLoading } = useRunbookTitle();
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newVersionDescription, setNewVersionDescription] = useState("");
  const [showValidateModal, setShowValidateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [compareMode, setCompareMode] = useState(false);
  const [compareVersion1, setCompareVersion1] = useState(null);
  const [compareVersion2, setCompareVersion2] = useState(null);
  const [showCompareModal, setShowCompareModal] = useState(false);
  const [pendingPublish, setPendingPublish] = useState(false);
  const [publishValidating, setPublishValidating] = useState(false);
  const [pendingActivate, setPendingActivate] = useState(false);
  const [activateValidating, setActivateValidating] = useState(false);

  // Extract queryParams so it's accessible throughout the component
  const queryParams = Utils.getQueryStringParams(location);

  // Get the runbook name from the store
  const currentRunbook = runbookReducer.paginatedResult?.data?.find(
    (r) => r.runbookId === queryParams.runbookId
  );

  useEffect(() => {
    dispatch(runbookVersionActions.get(queryParams));
    // eslint-disable-next-line
  }, [location]);

  // Handle publish after validation completes
  useEffect(() => {
    if (pendingPublish && !runbookReducer.loadingValidate) {
      setPublishValidating(false);

      if (runbookReducer.validateResponse && !runbookReducer.validateResponse.hasErrors) {
        // Validation passed - proceed with publish
        setPendingPublish(false);
        proceedWithPublish();
      } else if (runbookReducer.validateResponse?.hasErrors || runbookReducer.validateError) {
        // Validation failed - show the validation modal with errors
        setPendingPublish(false);
        setShowValidateModal(true);
      }
    }
    // eslint-disable-next-line
  }, [runbookReducer.loadingValidate, runbookReducer.validateResponse, runbookReducer.validateError, pendingPublish]);

  // Handle activate after validation completes
  useEffect(() => {
    if (pendingActivate && !runbookReducer.loadingValidate) {
      setActivateValidating(false);

      if (runbookReducer.validateResponse && !runbookReducer.validateResponse.hasErrors) {
        // Validation passed - proceed with activate
        setPendingActivate(false);
        proceedWithActivate();
      } else if (runbookReducer.validateResponse?.hasErrors || runbookReducer.validateError) {
        // Validation failed - show the validation modal with errors
        setPendingActivate(false);
        setShowValidateModal(true);
      }
    }
    // eslint-disable-next-line
  }, [runbookReducer.loadingValidate, runbookReducer.validateResponse, runbookReducer.validateError, pendingActivate]);

  let displayColumns = [
    "versionNum",
    "description",
    "createdBy",
    "createdOn",
    "lockedDisplay",
  ];

  const getStatusBadge = (status) => {
    switch (status) {
      case 'Draft':
        return <span className="badge bg-secondary">Draft</span>;
      case 'Published':
        return <span className="badge bg-info">Published</span>;
      case 'Active':
        return <span className="badge bg-success">Active</span>;
      default:
        return <span className="badge bg-light text-dark">{status || 'Unknown'}</span>;
    }
  };

  const data = runbookVersionReducer.data?.map(
    (version) => {
      return {
        ...version,
        createdOn: version.createdOn ? new Date(version.createdOn).toLocaleString() : '',
        runbookStatusDisplay: getStatusBadge(version.runbookStatus),
        lockedDisplay: version.locked ? (
          <span className="badge bg-warning text-dark">
            <FaLock className="me-1" />
            Locked
          </span>
        ) : (
          <span className="badge bg-light text-dark">
            <FaUnlock className="me-1" />
            Unlocked
          </span>
        ),
      };
    }
  );

  const loading = runbookTitleLoading || runbookVersionReducer.loading;

  // Check if there's a locked active version
  const hasLockedActiveVersion = runbookVersionReducer.data?.some(
    (version) => version.runbookStatus === 'Active' && version.locked
  );

  // #1977: any locked version blocks Create/Revert; locking is only allowed on the highest version.
  const hasLockedVersion = runbookVersionReducer.data?.some((version) => version.locked);
  const highestVersionNum = runbookVersionReducer.data?.length
    ? Math.max(...runbookVersionReducer.data.map((v) => v.versionNum))
    : null;
  const isHighestSelected = !!selectedVersion && selectedVersion.versionNum === highestVersionNum;

  // Check permissions
  if (!hasPermission("runbook-versions.view")) {
    return (
      <>
        <Title text="Runbook Versions" icon={<FaCodeBranch className="mb-1" />} />
        <Alert variant="danger">
          You do not have permission to view runbook versions.
        </Alert>
      </>
    );
  }

  const handleCreateVersion = () => {
    if (!selectedVersion) return;

    const requestBody = {
      runbookId: queryParams.runbookId,
      runbookGuid: queryParams.runbookGuid,
      versionNum: selectedVersion.versionNum,
      description: newVersionDescription,
      clientId: props.user.clientId,
      clientGuid: props.user.clientGuid,
    };

    dispatch(runbookVersionActions.create(requestBody));
    setShowCreateModal(false);
    setNewVersionDescription("");
    setSelectedVersion(null);
  };

  const handleToggleLock = async () => {
    if (!selectedVersion) return;

    const requestBody = {
      versionId: selectedVersion.versionId,
      runbookId: queryParams.runbookId,
      runbookGuid: queryParams.runbookGuid,
      locked: !selectedVersion.locked,
      clientId: props.user.clientId,
      clientGuid: props.user.clientGuid,
    };

    try {
      await runbookVersionService.update(requestBody);
      // Refresh the list
      dispatch(runbookVersionActions.get(queryParams));
      setSelectedVersion(null);
    } catch (error) {
      alert('Failed to update lock status. Please try again.');
    }
  };

  // Function to actually perform the publish after validation passes
  const proceedWithPublish = async () => {
    if (!selectedVersion) return;

    const requestBody = {
      versionId: selectedVersion.versionId,
      runbookId: queryParams.runbookId,
      runbookGuid: queryParams.runbookGuid,
      runbookStatus: 'Published',
      clientId: props.user.clientId,
      clientGuid: props.user.clientGuid,
    };

    try {
      await runbookVersionService.update(requestBody);
      // Refresh the list
      dispatch(runbookVersionActions.get(queryParams));
      setSelectedVersion(null);
    } catch (error) {
      alert('Failed to publish. Please try again.');
    }
  };

  // Function to actually perform the activate after validation passes
  const proceedWithActivate = async () => {
    if (!selectedVersion) return;

    const requestBody = {
      versionId: selectedVersion.versionId,
      runbookId: queryParams.runbookId,
      runbookGuid: queryParams.runbookGuid,
      runbookStatus: 'Active',
      clientId: props.user.clientId,
      clientGuid: props.user.clientGuid,
    };

    try {
      await runbookVersionService.update(requestBody);

      // After activation, call validate with UpdateRunbookHeader: true to update the header
      const validateRequestBody = {
        runbookId: queryParams.runbookId,
        clientId: props.user?.clientId,
        runbookGuid: queryParams.runbookGuid,
        clientGuid: props.user?.clientGuid,
        versionNum: selectedVersion.versionNum,
        UpdateRunbookHeader: true,
      };

      dispatch(runbookActions.validate(validateRequestBody));

      // Refresh the list
      dispatch(runbookVersionActions.get(queryParams));
      setSelectedVersion(null);
    } catch (error) {
      alert('Failed to activate. Please try again.');
    }
  };

  const handleChangeStatus = async (newStatus) => {
    if (!selectedVersion) return;

    // Validate status transitions
    if (newStatus === 'Draft' && (selectedVersion.runbookStatus === 'Published' || selectedVersion.runbookStatus === 'Active')) {
      alert('Cannot change a Published or Active version back to Draft.');
      return;
    }

    // For publishing, validate first
    if (newStatus === 'Published') {
      setPublishValidating(true);
      setPendingPublish(true);

      const requestBody = {
        runbookId: queryParams.runbookId,
        clientId: props.user?.clientId,
        runbookGuid: queryParams.runbookGuid,
        clientGuid: props.user?.clientGuid,
        versionNum: selectedVersion.versionNum,
        UpdateRunbookHeader: false,
      };

      dispatch(runbookActions.validate(requestBody));
      return;
    }

    // For activating, validate first with UpdateRunbookHeader: true
    if (newStatus === 'Active') {
      setActivateValidating(true);
      setPendingActivate(true);

      const requestBody = {
        runbookId: queryParams.runbookId,
        clientId: props.user?.clientId,
        runbookGuid: queryParams.runbookGuid,
        clientGuid: props.user?.clientGuid,
        versionNum: selectedVersion.versionNum,
        UpdateRunbookHeader: true,
      };

      dispatch(runbookActions.validate(requestBody));
      return;
    }

    const requestBody = {
      versionId: selectedVersion.versionId,
      runbookId: queryParams.runbookId,
      runbookGuid: queryParams.runbookGuid,
      runbookStatus: newStatus,
      clientId: props.user.clientId,
      clientGuid: props.user.clientGuid,
    };

    try {
      await runbookVersionService.update(requestBody);
      // Refresh the list
      dispatch(runbookVersionActions.get(queryParams));
      setSelectedVersion(null);
    } catch (error) {
      alert('Failed to change status. Please try again.');
    }
  };

  const handleDeleteClick = () => {
    if (!selectedVersion) return;

    setDeleteError(null);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedVersion) return;

    if (selectedVersion.locked || selectedVersion.runbookStatus === 'Active') {
      setDeleteError('Cannot delete this version. Active versions and locked versions cannot be deleted.');
      return;
    }

    setDeleting(true);
    setDeleteError(null);

    const requestParameters = {
      versionId: selectedVersion.versionId,
      runbookId: queryParams.runbookId,
      runbookGuid: queryParams.runbookGuid,
      clientId: props.user.clientId,
      clientGuid: props.user.clientGuid,
    };

    try {
      await runbookVersionService.deleteVersion(requestParameters);
      // Refresh the list
      dispatch(runbookVersionActions.get(queryParams));
      setSelectedVersion(null);
      setShowDeleteModal(false);
      setDeleting(false);
    } catch (error) {
      setDeleting(false);

      // Get error message from response
      let errorMessage = 'Failed to delete version. Please try again.';

      if (error.response?.data) {
        // Check if data is a string or object
        if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        } else if (error.response.data.message) {
          errorMessage = error.response.data.message;
        }
      }

      setDeleteError(errorMessage);
    }
  };

  const handleCompareClick = () => {
    setCompareMode(true);
    setCompareVersion1(null);
    setCompareVersion2(null);
  };

  const handleCancelCompare = () => {
    setCompareMode(false);
    setCompareVersion1(null);
    setCompareVersion2(null);
  };

  const handleVersionSelectForCompare = (version) => {
    if (!compareVersion1) {
      setCompareVersion1(version);
    } else if (!compareVersion2) {
      setCompareVersion2(version);
      // Auto-open comparison modal when both versions selected
      setTimeout(() => {
        setShowCompareModal(true);
      }, 100);
    } else {
      // Reset and start over
      setCompareVersion1(version);
      setCompareVersion2(null);
    }
  };

  const handleCloseCompareModal = () => {
    setShowCompareModal(false);
    setCompareMode(false);
    setCompareVersion1(null);
    setCompareVersion2(null);
  };

  const handleValidate = () => {
    if (!selectedVersion) return;

    setShowValidateModal(true);

    // #1977: RunbookTable.ISVALID tracks the LIVE (highest) version, so only update the header when
    // validating the highest version; validating an older version is informational only.
    const shouldUpdateHeader = isHighestSelected;

    const requestBody = {
      runbookId: queryParams.runbookId,
      clientId: props.user?.clientId,
      runbookGuid: queryParams.runbookGuid,
      clientGuid: props.user?.clientGuid,
      versionNum: selectedVersion.versionNum,
      UpdateRunbookHeader: shouldUpdateHeader,
    };

    dispatch(runbookActions.validate(requestBody));
  };

  // #1977: "Revert to version N" = create a new MAX+1 version that is a copy of version N
  // (the create endpoint already copies RUNBOOKSTRING from the given source version), then
  // validate the new latest version so it is runnable (highest-wins + must-validate).
  const handleRevertVersion = async () => {
    if (!selectedVersion) return;
    const sourceNum = selectedVersion.versionNum;
    try {
      await runbookVersionService.create({
        runbookId: queryParams.runbookId,
        runbookGuid: queryParams.runbookGuid,
        versionNum: sourceNum, // source version to copy forward
        description: `Reverted from version ${sourceNum}`,
        createdBy: props.user?.email,
        clientId: props.user.clientId,
        clientGuid: props.user.clientGuid,
      });

      // The reverted copy is a new MAX+1 version; find it and validate it (updates the header).
      const response = await runbookVersionService.get({
        runbookId: queryParams.runbookId,
        runbookGuid: queryParams.runbookGuid,
      });
      const versions = response?.data;
      const newVersionNum = Array.isArray(versions) && versions.length
        ? Math.max(...versions.map((v) => v.versionNum))
        : sourceNum;

      // Surface the validation result of the reverted (new highest) version — same modal the
      // Validate button uses. Open it BEFORE dispatching so the "validating…" state shows.
      setShowValidateModal(true);
      dispatch(runbookActions.validate({
        runbookId: queryParams.runbookId,
        clientId: props.user?.clientId,
        runbookGuid: queryParams.runbookGuid,
        clientGuid: props.user?.clientGuid,
        versionNum: newVersionNum,
        UpdateRunbookHeader: true,
      }));

      // Refresh the grid (new version + validity) — this updates a different reducer and does NOT
      // close the validate modal.
      dispatch(runbookVersionActions.get(queryParams));
      setSelectedVersion(null);
    } catch (error) {
      alert("Failed to revert. Please try again.");
    }
  };

  return (
    <>
      <Title text="Runbook Versions" icon={<FaCodeBranch className="mb-1" />} />
      {!loading && (
        <Subtitle text={runbookTitle} icon={<CiBoxList className="mb-1" />} showBackButton={false} />
      )}

      {/* Compare Mode Banner */}
      {compareMode && (
        <Alert variant="info" className="mb-3 d-flex justify-content-between align-items-center">
          <div>
            <strong>Compare Mode Active:</strong> Select 2 versions to compare
            {compareVersion1 && (
              <span className="ms-3">
                <Badge bg="primary">Version {compareVersion1.versionNum}</Badge>
              </span>
            )}
            {compareVersion2 && (
              <span className="ms-2">
                <Badge bg="primary">Version {compareVersion2.versionNum}</Badge>
              </span>
            )}
          </div>
          <Button variant="secondary" size="sm" onClick={handleCancelCompare}>
            Cancel Compare
          </Button>
        </Alert>
      )}

      {/* Action Buttons */}
      <div className="d-flex gap-2 mb-3 p-3 bg-light rounded"
           style={{
             opacity: selectedVersion || compareMode ? 1 : 0.6,
             transition: 'opacity 0.3s ease'
           }}>
        {hasPermission("runbook-versions.view") && !compareMode && (
          <Button
            variant="outline-primary"
            size="sm"
            onClick={handleCompareClick}
            title="Compare two versions side-by-side"
          >
            <FaExchangeAlt className="mb-1 me-2" />
            Compare Versions
          </Button>
        )}

        {/* Grouped Version Actions: View, Edit, Create, Delete */}
        {!compareMode && (
          <ButtonGroup size="sm">
            {hasPermission("runbook-versions.view") && (
              <Button
                variant="outline-primary"
                disabled={!selectedVersion}
                onClick={() => {
                  if (selectedVersion) {
                    sessionStorage.setItem('importWorkflow', selectedVersion.runbookString);
                    sessionStorage.setItem('editingVersion', JSON.stringify({
                      versionId: selectedVersion.versionId,
                      versionNum: selectedVersion.versionNum,
                      description: selectedVersion.description,
                      runbookId: queryParams.runbookId,
                      runbookGuid: queryParams.runbookGuid,
                      runbookName: currentRunbook?.runbookName || '',
                      readOnly: true
                    }));
                    navigate(`/runbook/workflow`);
                  }
                }}
                title={!selectedVersion ? "Select a version first" : "View this version (read-only)"}
              >
                <FaEdit className="mb-1 me-2" />
                View
              </Button>
            )}

            {hasPermission("runbook-versions.edit") && (
              <Button
                variant="outline-primary"
                disabled={!selectedVersion || selectedVersion.locked || selectedVersion.runbookStatus === 'Published' || selectedVersion.runbookStatus === 'Active'}
                onClick={() => {
                  if (selectedVersion && !selectedVersion.locked && selectedVersion.runbookStatus === 'Draft') {
                    // Store the version's runbook string and metadata in session storage for the workflow builder to load
                    sessionStorage.setItem('importWorkflow', selectedVersion.runbookString);
                    sessionStorage.setItem('editingVersion', JSON.stringify({
                      versionId: selectedVersion.versionId,
                      versionNum: selectedVersion.versionNum,
                      description: selectedVersion.description,
                      runbookId: queryParams.runbookId,
                      runbookGuid: queryParams.runbookGuid,
                      runbookName: currentRunbook?.runbookName || '',
                      readOnly: false
                    }));
                    navigate(`/runbook/workflow`);
                  }
                }}
                title={
                  !selectedVersion
                    ? "Select a version first"
                    : selectedVersion.locked
                      ? "Cannot edit a locked version"
                      : (selectedVersion.runbookStatus === 'Published' || selectedVersion.runbookStatus === 'Active')
                        ? "Cannot edit a Published or Active version"
                        : "Edit this version"
                }
              >
                <FaEdit className="mb-1 me-2" />
                Edit
              </Button>
            )}

            {/* #1977: Create removed — new versions come from "Apply" in the builder (or Revert). */}

            {hasPermission("runbook-versions.edit") && (
              <Button
                variant="outline-primary"
                disabled={!selectedVersion || hasLockedVersion || isHighestSelected}
                onClick={() => {
                  if (selectedVersion && !hasLockedVersion && !isHighestSelected && window.confirm(`Revert to version ${selectedVersion.versionNum}? This creates a new latest version that is an exact copy of it, then validates it so it becomes the one that runs.`)) {
                    handleRevertVersion();
                  }
                }}
                title={
                  !selectedVersion
                    ? "Select a version first"
                    : hasLockedVersion
                      ? "A version is locked — unlock it before reverting"
                      : isHighestSelected
                        ? "This is already the latest version — nothing to revert to"
                        : "Revert: create a new latest version as a copy of this one, then validate it"
                }
              >
                <FaExchangeAlt className="mb-1 me-2" />
                Revert
              </Button>
            )}

            {/* #1977: version deletion removed — versions are immutable history, never deleted. */}
          </ButtonGroup>
        )}

        {hasPermission("runbooks.validate") && !compareMode && (
          <Button
            variant="outline-primary"
            size="sm"
            disabled={!selectedVersion || !isHighestSelected}
            onClick={handleValidate}
            title={
              !selectedVersion
                ? "Select a version first"
                : !isHighestSelected
                  ? "Only the latest version can be validated"
                  : "Validate this version"
            }
          >
            <MdGppGood className="mb-1 me-2" />
            Validate
          </Button>
        )}

        {hasPermission("runbook-versions.lock") && !compareMode && (
          <Button
            variant="outline-primary"
            size="sm"
            disabled={!selectedVersion || (!selectedVersion.locked && !isHighestSelected)}
            onClick={handleToggleLock}
            title={
              !selectedVersion
                ? "Select a version first"
                : selectedVersion.locked
                  ? "Unlock this version to allow editing"
                  : !isHighestSelected
                    ? "Only the latest version can be locked"
                    : "Lock this version to prevent editing"
            }
          >
            {selectedVersion && selectedVersion.locked ? (
              <>
                <FaUnlock className="mb-1 me-2" />
                Unlock
              </>
            ) : (
              <>
                <FaLock className="mb-1 me-2" />
                Lock
              </>
            )}
          </Button>
        )}

        {/* #1977: Publish & Activate removed — the highest version is live automatically (no
            draft/active/published lifecycle). "Apply" in the builder is the only go-live action. */}

        {selectedVersion && (
          <div className="ms-auto align-self-center">
            <small className="text-muted">
              Selected: <strong>Version {selectedVersion.versionNum} - {selectedVersion.description}</strong>
            </small>
          </div>
        )}
      </div>

      <Grid
        data={data}
        displayColumns={displayColumns}
        idColumn={"versionId"}
        loading={loading}
        pageNumber={1}
        totalPages={data && data.length > 0 ? 1 : 0}
        selectedRunbook={compareMode ? (compareVersion1 || compareVersion2) : selectedVersion}
        onRowClick={(version) => {
          if (compareMode) {
            handleVersionSelectForCompare(version);
          } else {
            setSelectedVersion(version);
          }
        }}
      ></Grid>

      {/* Version Comparison Modal */}
      {showCompareModal && compareVersion1 && compareVersion2 && (
        <RunbookVersionCompare
          show={showCompareModal}
          onHide={handleCloseCompareModal}
          version1={compareVersion1}
          version2={compareVersion2}
        />
      )}

      {/* Create Version Modal */}
      <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Create New Version</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            This will create a new version based on <strong>Version {selectedVersion?.versionNum}</strong>.
          </p>
          <Form.Group className="mb-3">
            <Form.Label>Description for New Version</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={newVersionDescription}
              onChange={(e) => setNewVersionDescription(e.target.value)}
              placeholder="Enter description for the new version..."
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCreateModal(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleCreateVersion}
            disabled={!newVersionDescription.trim()}
          >
            Create Version
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Validation Results Modal */}
      <Modal
        show={showValidateModal}
        onHide={() => setShowValidateModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Validate Runbook - {queryParams.runbookId} (Version {selectedVersion?.versionNum})
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {runbookReducer.loadingValidate && (
            <>
              Validating version {selectedVersion?.versionNum}...
              <Spinner
                className="float-end"
                size="sm"
                animation="border"
                variant="primary"
              />
            </>
          )}

          {!runbookReducer.loadingValidate && (
            <>
              {runbookReducer.validateResponse && (
                <>
                  {!runbookReducer.validateResponse.hasErrors ? (
                    <div className="text-success">
                      <MdGppGood className="me-2 mb-1" />
                      Validation successful!
                    </div>
                  ) : (
                    <div className="text-danger">
                      <MdGppBad className="me-2 mb-1" />
                      {runbookReducer.validateResponse.message ||
                        "Validation failed. Please check the details."}
                    </div>
                  )}
                </>
              )}

              {runbookReducer.validateError && (
                <div className="text-danger">
                  <MdGppBad className="me-2 mb-1" />
                  Validation failed with an error. Please try again.
                </div>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowValidateModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        show={showDeleteModal}
        onHide={() => {
          if (!deleting) {
            setShowDeleteModal(false);
            setDeleteError(null);
          }
        }}
        centered
      >
        <Modal.Header closeButton={!deleting}>
          <Modal.Title className="text-danger">
            <FaTrash className="me-2 mb-1" />
            Delete Version
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {deleteError && (
            <Alert variant="danger" className="mb-3">
              {deleteError}
            </Alert>
          )}

          {!deleteError && (
            <Alert variant="warning">
              <strong>Warning:</strong> This action cannot be undone!
            </Alert>
          )}

          <p>Are you sure you want to delete the following version?</p>
          <div className="bg-light p-3 rounded">
            <div className="mb-2">
              <strong>Runbook:</strong> {queryParams.runbookId}
            </div>
            <div className="mb-2">
              <strong>Version:</strong> {selectedVersion?.versionNum}
            </div>
            <div className="mb-2">
              <strong>Description:</strong> {selectedVersion?.description || <em className="text-muted">No description</em>}
            </div>
            <div className="mb-2">
              <strong>Status:</strong> {selectedVersion && getStatusBadge(selectedVersion.runbookStatus)}
            </div>
            <div className="mb-2">
              <strong>Locked:</strong> {selectedVersion?.locked ? (
                <span className="badge bg-warning text-dark ms-2">
                  <FaLock className="me-1" />
                  Yes - Cannot Delete
                </span>
              ) : (
                <span className="badge bg-light text-dark ms-2">
                  <FaUnlock className="me-1" />
                  No
                </span>
              )}
            </div>
            {selectedVersion?.runbookStatus === 'Active' && (
              <div className="mb-0">
                <Alert variant="danger" className="mb-0 py-2">
                  <strong>Cannot Delete:</strong> Active versions cannot be deleted. Change the status to Published or Draft first.
                </Alert>
              </div>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setShowDeleteModal(false);
              setDeleteError(null);
            }}
            disabled={deleting}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleConfirmDelete}
            disabled={
              selectedVersion?.locked ||
              selectedVersion?.runbookStatus === 'Active' ||
              deleting
            }
          >
            {deleting ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-1"
                />
                Deleting...
              </>
            ) : (
              <>
                <FaTrash className="me-1" /> Delete Version
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default RunbookVersionGrid;
